import { cn } from '@/lib/utils';

interface KeyboardChordProps {
  keys: string[];
  /** Parallel array — true means that chip renders in the "pressed" state. */
  pressed?: boolean[];
  /** Parallel array — true means that chip renders in the "pulsing" state
   *  (animated ring, indicates "we're waiting for this key"). Mutually
   *  exclusive with `pressed`; if both are true on the same chip, `pressed`
   *  wins. */
  pulsing?: boolean[];
  label?: string;
  size?: 'md' | 'lg';
  /** Pressed-state colour scheme for chips marked in `pressed`.
   *  - `live` (default): blue, used while the user is holding the chord down
   *  - `locked`: emerald, used post-save while the success bar runs        */
  tone?: 'live' | 'locked';
}

/**
 * Renders a keyboard chord (⌘ + ⇧ + E) as a row of key-cap chips.
 *
 * Three visual states per chip:
 *   - default: neutral cap (gray border, white background)
 *   - pressed (tone=live):   blue cap, white glyph
 *   - pressed (tone=locked): emerald cap, white glyph (post-save success)
 *   - pulsing:               soft blue ring + subtle pulse, used for chips
 *                            the page can't observe directly (third key when
 *                            Chrome intercepts the extension chord) but
 *                            wants to nudge the user to press
 */
export function KeyboardChord({
  keys,
  pressed,
  pulsing,
  label,
  size = 'lg',
  tone = 'live',
}: KeyboardChordProps) {
  const chipBase =
    size === 'lg'
      ? 'h-12 min-w-12 px-3 text-base'
      : 'h-9 min-w-9 px-2 text-sm';

  const pressedClass =
    tone === 'locked'
      ? 'border border-emerald-500 bg-emerald-500/90 text-white'
      : 'border border-blue-600 bg-blue-600 text-white';

  return (
    <div className="inline-flex flex-wrap items-center gap-2.5">
      {keys.map((key, i) => {
        const isPressed = pressed?.[i] ?? false;
        const isPulsing = !isPressed && (pulsing?.[i] ?? false);
        return (
          <span
            key={i}
            className={cn(
              'relative inline-flex items-center justify-center rounded-sm font-mono font-medium shadow-sm transition-colors duration-100',
              chipBase,
              isPressed
                ? pressedClass
                : isPulsing
                  ? 'border border-blue-300 bg-blue-50 text-blue-700'
                  : 'border border-neutral-200 bg-white text-neutral-700'
            )}
          >
            {key}
            {isPulsing && (
              <span
                aria-hidden="true"
                className="pointer-events-none absolute inset-0 rounded-sm ring-2 ring-blue-400/60 motion-safe:animate-pulse"
              />
            )}
          </span>
        );
      })}
      {label && (
        <span className="ml-3 text-base font-normal text-neutral-600">
          {label}
        </span>
      )}
    </div>
  );
}
